import { FileLinkPicker, type PickedFile } from "@/components/ui/file-link-picker";
import { Markdown } from "@/components/ui/markdown";
import { toast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import {
  Bold,
  Code,
  Eye,
  EyeOff,
  FileSymlink,
  Heading2,
  Image,
  Italic,
  Link2,
  List,
  ListChecks,
  ListOrdered,
  Loader2,
  Paperclip,
  Quote,
  Table as TableIcon,
} from "lucide-react";
import { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * Markdown editor — toolbar shortcuts, a live preview toggle,
 * `@<query>` mention autocomplete, and (when an `uploadEndpoint`
 * is given) image / file upload via the toolbar button, paste, or
 * drag-drop. Controlled component (`value` + `onChange`).
 */

export type MentionUser = { id: string; name: string; username: string | null };

type Props = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  /** Grow the textarea with its content (`rows` stays the minimum). */
  autoGrow?: boolean;
  /** CSS max-height for the grown textarea (e.g. `70vh`); it scrolls inside beyond it. */
  autoGrowMaxHeight?: string;
  className?: string;
  /** Pool for `@<query>` autocomplete. Omit to disable mentions. */
  mentionUsers?: MentionUser[];
  /**
   * Upload endpoint for paste / drop / the toolbar upload button.
   * `/api/uploads` for Hub-local storage, `/api/work-items/:id/
   * uploads` for GitLab-backed. Omit to disable uploads.
   */
  uploadEndpoint?: string | undefined;
  /** Id of the project when it has a Seafile folder: `[[` and the toolbar icon then link a file. */
  fileLinks?: string | null;
};

type Shortcut = {
  icon: React.ElementType;
  label: string;
  prefix: string;
  suffix: string;
  block?: boolean;
  /** Multi-line selection / empty line gets a ``` fenced block instead. */
  fence?: boolean;
};

const SHORTCUTS: Shortcut[] = [
  { icon: Bold, label: "bold", prefix: "**", suffix: "**" },
  { icon: Italic, label: "italic", prefix: "_", suffix: "_" },
  { icon: Heading2, label: "heading", prefix: "## ", suffix: "", block: true },
  { icon: Code, label: "code", prefix: "`", suffix: "`", fence: true },
  { icon: Quote, label: "quote", prefix: "> ", suffix: "", block: true },
  { icon: List, label: "bulletList", prefix: "- ", suffix: "", block: true },
  { icon: ListOrdered, label: "numberedList", prefix: "1. ", suffix: "", block: true },
  { icon: ListChecks, label: "checklist", prefix: "- [ ] ", suffix: "", block: true },
  { icon: Link2, label: "link", prefix: "[", suffix: "](url)" },
  { icon: Image, label: "image", prefix: "![alt](", suffix: ")" },
];

export function MarkdownEditor({
  value,
  onChange,
  placeholder,
  rows = 6,
  autoGrow = false,
  autoGrowMaxHeight,
  className,
  mentionUsers,
  uploadEndpoint,
  fileLinks = null,
}: Props) {
  const { t } = useTranslation();
  const ref = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  // Open file picker: index of the typed `[[` to replace, or -1 when opened from the toolbar.
  const [pickerAt, setPickerAt] = useState<number | null>(null);
  const mentionsOn = (mentionUsers?.length ?? 0) > 0;
  const helpParts = [
    ...(mentionsOn ? [t("workItemDetail.editor.helpMention")] : []),
    ...(fileLinks ? [t("workItemDetail.editor.helpFile")] : []),
    t("workItemDetail.editor.helpMarkdown"),
  ];
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measure when value or preview changes
  useLayoutEffect(() => {
    const ta = ref.current;
    if (!autoGrow || !ta) return;
    ta.style.height = "auto";
    ta.style.height = `${ta.scrollHeight + ta.offsetHeight - ta.clientHeight}px`;
  }, [autoGrow, value, preview]);
  const [uploading, setUploading] = useState(false);

  /** Upload a file to `uploadEndpoint` and splice its markdown at the cursor. */
  async function uploadFile(file: File) {
    if (!uploadEndpoint) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(uploadEndpoint, {
        method: "POST",
        body: fd,
        credentials: "include",
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        toast({
          title: t("workItemDetail.editor.uploadFailed"),
          description: data.error ?? `Server returned ${res.status}`,
          variant: "destructive",
        });
        return;
      }
      const { markdown } = (await res.json()) as { markdown: string };
      const ta = ref.current;
      const pos = ta?.selectionStart ?? value.length;
      onChange(`${value.slice(0, pos)}${markdown}\n${value.slice(pos)}`);
    } catch (err) {
      toast({
        title: t("workItemDetail.editor.uploadFailed"),
        description: err instanceof Error ? err.message : t("workItemDetail.editor.networkError"),
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  }

  function onPaste(e: React.ClipboardEvent) {
    if (!uploadEndpoint) return;
    for (const item of e.clipboardData?.items ?? []) {
      if (item.type.startsWith("image/")) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) void uploadFile(file);
        return;
      }
    }
  }

  function onDrop(e: React.DragEvent) {
    if (!uploadEndpoint || !e.dataTransfer?.files?.length) return;
    e.preventDefault();
    for (const file of e.dataTransfer.files) void uploadFile(file);
  }

  // `@<query>` autocomplete state — `mention.start` is the index of the
  // `@` in `value`; on select we replace [start, cursor) with `@handle `.
  const [mention, setMention] = useState<{ start: number; query: string } | null>(null);
  const [mentionIdx, setMentionIdx] = useState(0);

  function wrap(shortcut: Shortcut) {
    const ta = ref.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;

    if (shortcut.block) {
      // Block shortcuts (heading / quote / list) prefix every line the
      // selection touches — the selected text is kept verbatim, never
      // replaced. The line(s) span from the start of the first line.
      const lineStart = value.lastIndexOf("\n", start - 1) + 1;
      const region = value.slice(lineStart, end);
      const prefixed = region
        .split("\n")
        .map((line) => `${shortcut.prefix}${line}`)
        .join("\n");
      onChange(value.slice(0, lineStart) + prefixed + value.slice(end));
      requestAnimationFrame(() => {
        ta.focus();
        const cursor = lineStart + prefixed.length;
        ta.setSelectionRange(cursor, cursor);
      });
      return;
    }

    if (shortcut.fence) {
      // A trailing newline in the selection (whole-line select) would
      // leave a blank line before the closing fence — keep it outside.
      const fenceEnd = end > start && value[end - 1] === "\n" ? end - 1 : end;
      const selected = value.slice(start, fenceEnd);
      const lineStart = value.lastIndexOf("\n", start - 1) + 1;
      const nl = value.indexOf("\n", fenceEnd);
      const lineEnd = nl === -1 ? value.length : nl;
      const onEmptyLine = selected === "" && value.slice(lineStart, lineEnd).trim() === "";
      if (selected.includes("\n") || onEmptyLine) {
        // Fences must sit on their own lines, even mid-line selections.
        const before = value.slice(0, start);
        const after = value.slice(fenceEnd);
        const lead = before === "" || before.endsWith("\n") ? "" : "\n";
        const trail = after === "" || after.startsWith("\n") ? "" : "\n";
        onChange(`${before}${lead}\`\`\`\n${selected}\n\`\`\`${trail}${after}`);
        requestAnimationFrame(() => {
          ta.focus();
          const selStart = start + lead.length + 4;
          ta.setSelectionRange(selStart, selStart + selected.length);
        });
        return;
      }
    }

    // Inline shortcuts wrap the current selection; with no selection
    // they drop in a placeholder and leave it selected to type over.
    const hasSelection = end > start;
    const selected = hasSelection ? value.slice(start, end) : shortcut.label.toLowerCase();
    onChange(
      value.slice(0, start) + shortcut.prefix + selected + shortcut.suffix + value.slice(end),
    );
    requestAnimationFrame(() => {
      ta.focus();
      const selStart = start + shortcut.prefix.length;
      ta.setSelectionRange(selStart, selStart + selected.length);
    });
  }

  function insertTable() {
    const ta = ref.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;

    const lineStart = value.lastIndexOf("\n", start - 1) + 1;
    const currentLine = value.slice(lineStart, start);
    // GFM tables need a blank line above.
    const leading = currentLine.trim().length > 0 ? "\n\n" : start === 0 ? "" : "\n";

    const template = "| Column 1 | Column 2 |\n| --- | --- |\n| Cell | Cell |\n";
    const next = value.slice(0, start) + leading + template + value.slice(end);
    onChange(next);

    const headerOffset = start + leading.length + "| ".length;
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(headerOffset, headerOffset + "Column 1".length);
    });
  }

  function detectMention(text: string, cursor: number): { start: number; query: string } | null {
    let i = cursor - 1;
    while (i >= 0 && /[A-Za-z0-9._-]/.test(text[i] ?? "")) i--;
    if (i < 0 || text[i] !== "@") return null;
    const before = i === 0 ? "" : (text[i - 1] ?? "");
    if (before && !/\s/.test(before)) return null;
    return { start: i, query: text.slice(i + 1, cursor) };
  }

  function recomputeMention(text: string, cursor: number) {
    if (!mentionUsers || mentionUsers.length === 0) {
      setMention(null);
      return;
    }
    setMention(detectMention(text, cursor));
    setMentionIdx(0);
  }

  const filteredMentions = mention
    ? (mentionUsers ?? [])
        .filter((u) => {
          const q = mention.query.toLowerCase();
          if (!q) return true;
          return (u.username ?? "").toLowerCase().includes(q) || u.name.toLowerCase().includes(q);
        })
        .slice(0, 6)
    : [];

  function insertMention(u: MentionUser) {
    const ta = ref.current;
    if (!ta || !mention) return;
    const handle = u.username ?? u.name.replace(/\s+/g, "_");
    const insertion = `@${handle} `;
    const before = value.slice(0, mention.start);
    const after = value.slice(ta.selectionEnd);
    onChange(`${before}${insertion}${after}`);
    setMention(null);
    requestAnimationFrame(() => {
      ta.focus();
      const cursor = before.length + insertion.length;
      ta.setSelectionRange(cursor, cursor);
    });
  }

  function insertFileLink(picked: PickedFile) {
    const ta = ref.current;
    if (!fileLinks || !ta) return;
    const name = picked.name.replace(/[[\]]/g, "");
    const link = `[${name}](/file-link/${encodeURIComponent(fileLinks)}?path=${encodeURIComponent(picked.path)}) `;
    const typed = pickerAt !== null && pickerAt >= 0;
    const start = typed ? pickerAt : ta.selectionStart;
    const end = typed ? pickerAt + 2 : ta.selectionEnd;
    onChange(`${value.slice(0, start)}${link}${value.slice(end)}`);
    setPickerAt(null);
    requestAnimationFrame(() => {
      ta.focus();
      const cursor = start + link.length;
      ta.setSelectionRange(cursor, cursor);
    });
  }

  function onMentionKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (!mention || filteredMentions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setMentionIdx((i) => (i + 1) % filteredMentions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setMentionIdx((i) => (i - 1 + filteredMentions.length) % filteredMentions.length);
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      const pick = filteredMentions[mentionIdx];
      if (pick) insertMention(pick);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setMention(null);
    }
  }

  return (
    <div className={cn("rounded-md border border-input bg-background", className)}>
      <div className="flex items-center gap-0.5 border-b border-input px-2 py-1.5 flex-wrap">
        {SHORTCUTS.map((s) => (
          <button
            key={s.label}
            type="button"
            title={t(`workItemDetail.editor.${s.label}`)}
            tabIndex={-1}
            // Keep focus (and the selection) on the textarea — without
            // this the button steals focus and the selection is lost.
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => wrap(s)}
            className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <s.icon className="h-3.5 w-3.5" />
          </button>
        ))}
        {fileLinks && (
          <button
            type="button"
            data-testid="file-link-button"
            title={t("workItemDetail.editor.insertFileLink")}
            tabIndex={-1}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setPickerAt((p) => (p === null ? -1 : null))}
            className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <FileSymlink className="h-3.5 w-3.5" />
          </button>
        )}
        <button
          type="button"
          title={t("workItemDetail.editor.insertTable")}
          tabIndex={-1}
          onMouseDown={(e) => e.preventDefault()}
          onClick={insertTable}
          className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
        >
          <TableIcon className="h-3.5 w-3.5" />
        </button>

        {uploadEndpoint && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              data-testid="markdown-upload-input"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void uploadFile(file);
                e.target.value = "";
              }}
            />
            <button
              type="button"
              title={t("workItemDetail.editor.attachFile")}
              tabIndex={-1}
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            >
              {uploading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Paperclip className="h-3.5 w-3.5" />
              )}
            </button>
          </>
        )}

        <button
          type="button"
          title={preview ? t("workItemDetail.editor.edit") : t("workItemDetail.editor.preview")}
          tabIndex={-1}
          onClick={() => setPreview((p) => !p)}
          className="ml-auto flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
        >
          {preview ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
        </button>
      </div>

      {preview ? (
        <div className="min-h-[120px] px-3 py-2">
          {value ? (
            <Markdown content={value} />
          ) : (
            <p className="text-sm text-muted-foreground italic">
              {t("workItemDetail.editor.nothingToPreview")}
            </p>
          )}
        </div>
      ) : (
        <div className="relative">
          <textarea
            ref={ref}
            value={value}
            onChange={(e) => {
              onChange(e.target.value);
              recomputeMention(e.target.value, e.target.selectionStart);
              const cur = e.target.selectionStart;
              if (fileLinks && e.target.value.slice(cur - 2, cur) === "[[") setPickerAt(cur - 2);
              else if (pickerAt !== null) setPickerAt(null);
            }}
            onKeyUp={(e) => {
              if (["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) {
                const ta = e.currentTarget;
                recomputeMention(ta.value, ta.selectionStart);
              }
            }}
            onClick={(e) => {
              const ta = e.currentTarget;
              recomputeMention(ta.value, ta.selectionStart);
            }}
            onKeyDown={onMentionKeyDown}
            onBlur={() => {
              // Close after a tick so a dropdown click still registers.
              setTimeout(() => setMention(null), 120);
            }}
            onPaste={onPaste}
            onDrop={onDrop}
            onDragOver={(e) => {
              if (uploadEndpoint && e.dataTransfer?.types?.includes("Files")) {
                e.preventDefault();
              }
            }}
            placeholder={placeholder}
            rows={rows}
            style={
              autoGrow && autoGrowMaxHeight
                ? {
                    // Never below the `rows` minimum (20px line + 16px padding), whatever the window height.
                    maxHeight: `max(${rows * 1.25 + 1}rem, ${autoGrowMaxHeight})`,
                    overflowY: "auto",
                  }
                : undefined
            }
            className="flex w-full bg-transparent px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none resize-y font-mono"
          />
          {fileLinks && pickerAt !== null && (
            <FileLinkPicker
              projectId={fileLinks}
              onPick={insertFileLink}
              onClose={() => setPickerAt(null)}
            />
          )}
          {mention && filteredMentions.length > 0 && (
            <ul
              data-testid="mention-autocomplete"
              className="absolute left-3 z-30 mt-1 max-h-56 w-64 overflow-auto rounded-md border border-input bg-popover text-popover-foreground shadow-lg"
            >
              {filteredMentions.map((u, i) => (
                <li key={u.id}>
                  <button
                    type="button"
                    tabIndex={-1}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      insertMention(u);
                    }}
                    className={cn(
                      "flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm transition-colors",
                      i === mentionIdx ? "bg-accent text-accent-foreground" : "hover:bg-accent/40",
                    )}
                  >
                    <span className="font-medium">{u.name}</span>
                    <span className="text-xs text-muted-foreground">
                      @{u.username ?? u.name.replace(/\s+/g, "_")}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <div className="flex items-center gap-2 border-t border-input px-3 py-1.5 text-xs text-muted-foreground">
        <span data-testid="editor-help-line">{helpParts.join(" · ")}</span>
        <button
          type="button"
          data-testid="editor-help-toggle"
          aria-expanded={helpOpen}
          title={t("workItemDetail.editor.helpToggle")}
          tabIndex={-1}
          onClick={() => setHelpOpen((o) => !o)}
          className="flex h-5 w-5 items-center justify-center rounded-full border border-input hover:bg-muted hover:text-foreground"
        >
          ?
        </button>
      </div>
      {helpOpen && (
        <div
          data-testid="editor-help-panel"
          className="space-y-1 border-t border-input px-3 py-2 text-xs text-muted-foreground"
        >
          {mentionsOn && <p>{t("workItemDetail.editor.helpMentionLong")}</p>}
          {fileLinks && <p>{t("workItemDetail.editor.helpFileLong")}</p>}
          <p>{t("workItemDetail.editor.helpMarkdownLong")}</p>
        </div>
      )}
    </div>
  );
}
